<template>
  <Teleport to="body">
    <!-- 오버레이 배경 -->
    <Transition name="overlay">
      <div
        v-if="visible"
        class="splash-backdrop"
        @click="handleBackdropClick"
      ></div>
    </Transition>

    <!-- 반응형 컨테이너 (웹: 팝업, 모바일: 바텀시트) -->
    <Transition :name="isMobile ? 'sheet' : 'popup'">
      <div v-if="visible" :class="containerClass" @click.stop>
        <!-- 광고가 있을 때 -->
        <template v-if="adData">
          <div class="ad-content" @click="handleAdClick">
            <!-- 닫기 버튼 -->
            <button class="close-btn" @click.stop="skipAd">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <!-- 광고 이미지 (4:3 통일) -->
            <div class="ad-image-wrapper">
              <img
                :src="adData.imageUrl"
                :alt="adData.title"
                class="ad-image"
              />
              <!-- 그라데이션 -->
              <div class="ad-gradient"></div>

              <!-- 배지 -->
              <div class="ad-badges">
                <span class="ad-badge">AD</span>
                <span v-if="adData.region" class="ad-region">{{ adData.region }}</span>
              </div>
            </div>

            <!-- 광고 정보 -->
            <div class="ad-info">
              <h3 class="ad-title">{{ adData.title }}</h3>
              <p v-if="adData.subtitle" class="ad-subtitle">{{ adData.subtitle }}</p>

              <!-- CTA 버튼 -->
              <button class="ad-cta" @click.stop="handleAdClick">
                자세히 보기
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            <!-- 카운트다운 바 -->
            <div v-if="countdown > 0" class="countdown-bar">
              <div class="countdown-progress" :style="{ width: `${(countdown / 3) * 100}%` }"></div>
            </div>
          </div>
        </template>

        <!-- 기본 스플래시 (광고 없을 때) -->
        <template v-else>
          <div class="default-splash">
            <!-- 닫기 버튼 (웹에서만) -->
            <button v-if="!isMobile" class="close-btn-default" @click="closeSplash">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <!-- 로고 영역 -->
            <div class="splash-logo-section">
              <div class="splash-logo">
                <span class="logo-icon">🦭</span>
              </div>
              <h1 class="splash-title">물개</h1>
              <p class="splash-subtitle">분양 상담 매칭 플랫폼</p>
            </div>

            <!-- 로딩 -->
            <div class="splash-loader">
              <div class="loader-dot"></div>
              <div class="loader-dot"></div>
              <div class="loader-dot"></div>
            </div>

            <!-- 하단 문구 -->
            <p class="splash-footer">믿을 수 있는 분양 상담</p>
          </div>
        </template>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useSplashAd } from '@/composables/main/useSplashAd'

export default {
  name: 'SplashScreen',
  emits: ['complete'],
  setup(_, { emit }) {
    const router = useRouter()
    const { getActiveSplashAd, hasShownInSession, markAsShown, incrementImpressions, incrementClicks } = useSplashAd()

    const visible = ref(false)
    const adData = ref(null)
    const countdown = ref(3)
    const canSkip = ref(false)
    const windowWidth = ref(window.innerWidth)

    // 반응형 체크 (768px 기준)
    const isMobile = computed(() => windowWidth.value < 768)

    const containerClass = computed(() => {
      return isMobile.value ? 'splash-sheet' : 'splash-popup'
    })

    const handleResize = () => {
      windowWidth.value = window.innerWidth
    }

    const closeSplash = () => {
      visible.value = false
      markAsShown()
      setTimeout(() => {
        emit('complete')
      }, 300)
    }

    const skipAd = () => {
      if (countdown.value <= 0 || canSkip.value) {
        closeSplash()
      }
    }

    const handleBackdropClick = () => {
      // 카운트다운 끝난 후 배경 클릭으로 닫기
      if (countdown.value <= 0 || canSkip.value || !adData.value) {
        closeSplash()
      }
    }

    const handleAdClick = async () => {
      if (adData.value && adData.value.campaignCode) {
        await incrementClicks(adData.value.id)
        closeSplash()
        router.push(`/p/${adData.value.campaignCode}?from=splash`)
      }
    }

    onMounted(async () => {
      window.addEventListener('resize', handleResize)

      // 세션에서 이미 봤는지 확인
      if (hasShownInSession()) {
        emit('complete')
        return
      }

      // 광고 로드 시도
      const ad = await getActiveSplashAd()

      if (ad) {
        adData.value = ad
        visible.value = true
        await incrementImpressions(ad.id)

        // 카운트다운 (3초)
        const timer = setInterval(() => {
          countdown.value--
          if (countdown.value <= 0) {
            clearInterval(timer)
            canSkip.value = true
          }
        }, 1000)

        // 8초 후 자동 닫기
        setTimeout(() => {
          if (visible.value) closeSplash()
        }, 8000)
      } else {
        // 광고 없으면 기본 스플래시
        visible.value = true
        canSkip.value = true

        // 2초 후 자동 닫기
        setTimeout(() => {
          closeSplash()
        }, 2000)
      }
    })

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize)
    })

    return {
      visible,
      adData,
      countdown,
      canSkip,
      isMobile,
      containerClass,
      skipAd,
      closeSplash,
      handleBackdropClick,
      handleAdClick
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";

/* 배경 오버레이 */
.splash-backdrop {
  @apply fixed inset-0 z-[9998] bg-black/50;
}

/* ===== 웹: 중앙 팝업 ===== */
.splash-popup {
  @apply fixed z-[9999];
  @apply top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2;
  @apply w-full max-w-sm;
  @apply px-4;
}

/* ===== 모바일: 바텀 시트 ===== */
.splash-sheet {
  @apply fixed z-[9999];
  @apply bottom-0 left-0 right-0;
  padding-bottom: env(safe-area-inset-bottom, 0);
}

/* ===== 광고 콘텐츠 ===== */
.ad-content {
  @apply relative bg-white overflow-hidden shadow-2xl cursor-pointer;
}

.splash-popup .ad-content {
  @apply rounded-2xl;
}

.splash-sheet .ad-content {
  @apply rounded-t-3xl;
}

/* 이미지 영역 (4:3 통일) */
.ad-image-wrapper {
  @apply relative aspect-[4/3];
}

.ad-image {
  @apply w-full h-full object-cover;
}

.ad-gradient {
  @apply absolute inset-0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.2) 0%,
    transparent 40%,
    transparent 60%,
    rgba(0, 0, 0, 0.4) 100%
  );
}

/* 닫기 버튼 */
.close-btn {
  @apply absolute top-3 right-3 z-10;
  @apply w-8 h-8 rounded-full bg-black/40 text-white backdrop-blur-sm;
  @apply flex items-center justify-center;
  @apply hover:bg-black/60 transition;
}

/* 배지 */
.ad-badges {
  @apply absolute top-3 left-3 flex items-center gap-2;
}

.ad-badge {
  @apply px-2 py-0.5 text-xs font-bold bg-blue-600 text-white rounded;
}

.ad-region {
  @apply px-2 py-0.5 text-xs font-medium bg-white/90 text-slate-700 rounded;
}

/* 광고 정보 */
.ad-info {
  @apply p-4;
}

.splash-popup .ad-info {
  @apply p-5;
}

.ad-title {
  @apply text-base font-bold text-slate-900 mb-1;
}

.splash-popup .ad-title {
  @apply text-lg;
}

.ad-subtitle {
  @apply text-sm text-slate-500 mb-3;
}

.splash-popup .ad-subtitle {
  @apply mb-4;
}

.ad-cta {
  @apply w-full py-3 bg-blue-600 text-white font-semibold rounded-xl;
  @apply flex items-center justify-center gap-1;
  @apply hover:bg-blue-700 active:bg-blue-700 transition;
}

/* 카운트다운 바 */
.countdown-bar {
  @apply absolute bottom-0 left-0 right-0 h-1 bg-slate-200;
}

.countdown-progress {
  @apply h-full bg-blue-600 transition-all duration-1000 ease-linear;
}

/* ===== 기본 스플래시 (광고 없을 때) ===== */
.default-splash {
  @apply relative bg-gradient-to-b from-blue-600 to-blue-700 overflow-hidden;
  @apply flex flex-col items-center justify-center;
}

.splash-popup .default-splash {
  @apply rounded-2xl py-12 px-8;
}

.splash-sheet .default-splash {
  @apply rounded-t-3xl py-10 px-6;
}

.close-btn-default {
  @apply absolute top-3 right-3 z-10;
  @apply w-8 h-8 rounded-full bg-white/20 text-white backdrop-blur-sm;
  @apply flex items-center justify-center;
  @apply hover:bg-white/30 transition;
}

.splash-logo-section {
  @apply flex flex-col items-center mb-6;
}

.splash-logo {
  @apply w-20 h-20 bg-white rounded-2xl flex items-center justify-center shadow-xl mb-4;
  animation: logoScale 0.5s ease-out;
}

.logo-icon {
  @apply text-4xl;
}

.splash-title {
  @apply text-2xl font-bold text-white mb-1;
  animation: fadeInUp 0.5s ease-out 0.1s both;
}

.splash-subtitle {
  @apply text-blue-200 text-sm;
  animation: fadeInUp 0.5s ease-out 0.2s both;
}

.splash-loader {
  @apply flex gap-1.5 mb-4;
  animation: fadeInUp 0.5s ease-out 0.3s both;
}

.loader-dot {
  @apply w-1.5 h-1.5 bg-white rounded-full;
  animation: dotPulse 1.4s infinite ease-in-out both;
}

.loader-dot:nth-child(1) {
  animation-delay: -0.32s;
}

.loader-dot:nth-child(2) {
  animation-delay: -0.16s;
}

.splash-footer {
  @apply text-blue-200/80 text-xs;
  animation: fadeInUp 0.5s ease-out 0.4s both;
}

/* ===== 애니메이션 ===== */
@keyframes logoScale {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes fadeInUp {
  0% {
    transform: translateY(10px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes dotPulse {
  0%, 80%, 100% {
    transform: scale(0.6);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* ===== 트랜지션 ===== */
/* 오버레이 페이드 */
.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.3s ease;
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

/* 팝업 (웹) - 페이드만, 위치 이동 없음 */
.popup-enter-active,
.popup-leave-active {
  transition: opacity 0.3s ease;
}

.popup-enter-from,
.popup-leave-to {
  opacity: 0;
}

/* 바텀 시트 (모바일) - 슬라이드 업 */
.sheet-enter-active {
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.sheet-leave-active {
  transition: transform 0.25s ease-in;
}

.sheet-enter-from,
.sheet-leave-to {
  transform: translateY(100%);
}
</style>
